/**
 * Hook para calendario unificado (citas + llamadas + tareas)
 * @module hooks/useUnifiedCalendar
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { logger } from '@/utils/logger';
import type { CalendarEvent, Call, Task } from '@/types/calendar.types';
import type { AppointmentSlot } from '@/types/appointment.types';

/** Longitud máxima del nombre del cliente en el título del evento (evita desborde en celdas) */
const MAX_CLIENT_NAME_LENGTH = 28;

/** Trunca el nombre del cliente para mostrarlo en la celda del calendario */
function truncateClientName(name: string): string {
  const t = name.trim();
  if (t.length <= MAX_CLIENT_NAME_LENGTH) return t;
  return t.slice(0, MAX_CLIENT_NAME_LENGTH - 3) + '...';
}

/** Respuesta mínima de contacto para extraer nombre (GET /crm/contacts/:id) */
interface ContactNameResponse {
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

function getContactDisplayName(c: ContactNameResponse): string {
  if (c.name && c.name.trim()) return c.name.trim();
  const first = (c.first_name ?? '').trim();
  const last = (c.last_name ?? '').trim();
  const full = [first, last].filter(Boolean).join(' ');
  return full || 'Cliente';
}

/** Tipos de entidad que podemos enriquecer (tienen endpoint GET por ID y campo nombre) */
const LOADABLE_ENTITY_TYPES = ['contacts', 'leads', 'companies'] as const;
type LoadableEntityType = (typeof LOADABLE_ENTITY_TYPES)[number];

function isLoadableEntityType(
  value: string | null | undefined
): value is LoadableEntityType {
  return value != null && LOADABLE_ENTITY_TYPES.includes(value as LoadableEntityType);
}

interface UseUnifiedCalendarParams {
  start_date: string;
  end_date: string;
  delegation_id?: string;
  user_id?: string; // Filtrar por usuario responsable (agent_id en citas, responsible_user_id en tareas/llamadas)
  googleConnected?: boolean; // Reserved for FreeBusy checks only (events not shown in calendar)
}

export const useUnifiedCalendar = (params: UseUnifiedCalendarParams) => {
  return useQuery({
    queryKey: ['unified-calendar', params],
    queryFn: async (): Promise<CalendarEvent[]> => {
      // Construir params específicos para cada endpoint
      const baseParams = {
        start_date: params.start_date,
        end_date: params.end_date,
        delegation_id: params.delegation_id,
      };

      // Para citas, el campo es agent_id; para calls/tasks es responsible_user_id
      const appointmentParams = {
        ...baseParams,
        ...(params.user_id ? { agent_id: params.user_id } : {}),
      };
      const callTaskParams = {
        ...baseParams,
        ...(params.user_id ? { responsible_user_id: params.user_id } : {}),
      };

      // Fetch appointments, calls, and tasks in parallel
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        api
          .get<AppointmentSlot[]>('/crm/appointments/calendar', { params: appointmentParams })
          .then((r) => r.data)
          .catch((err) => {
            logger.warn('Error fetching appointments for calendar:', err);
            return [];
          }),
        api
          .get<Call[]>('/crm/calls/calendar', { params: callTaskParams })
          .then((r) => r.data)
          .catch((err) => {
            logger.warn('Error fetching calls for calendar:', err);
            return [];
          }),
        api
          .get<Task[]>('/crm/tasks/calendar', { params: callTaskParams })
          .then((r) => r.data)
          .catch((err) => {
            logger.warn('Error fetching tasks for calendar:', err);
            return [];
          }),
      ]);

      const appointments =
        results[0].status === 'fulfilled' ? results[0].value : [];
      const callsRaw = results[1].status === 'fulfilled' ? results[1].value : null;
      const calls = Array.isArray(callsRaw)
        ? callsRaw
        : (callsRaw && typeof callsRaw === 'object' && 'items' in callsRaw && Array.isArray((callsRaw as { items: Call[] }).items))
          ? (callsRaw as { items: Call[] }).items
          : [];
      const tasks = results[2].status === 'fulfilled' ? results[2].value : [];

      // Enriquecer llamadas sin contact_name: cargar nombres por contact_id / entity_id (solo cuando entity_type es conocido)
      const entityNames: Record<string, string> = {};
      type IdToLoad = { id: string; entityType: LoadableEntityType };
      const idsToLoad: IdToLoad[] = [];
      const seen = new Set<string>();
      calls.forEach((call) => {
        if (call.contact_name && (call.entity_id || call.contact_id)) {
          if (call.entity_id) entityNames[call.entity_id] = call.contact_name;
          if (call.contact_id && call.contact_id !== call.entity_id)
            entityNames[call.contact_id] = call.contact_name;
        }
        if (!call.contact_name && (call.contact_id || call.entity_id)) {
          const id = call.contact_id || call.entity_id!;
          // Solo enriquecer cuando entity_type es uno de los soportados; evita llamar a /contacts con ID de company o null
          const entityType = isLoadableEntityType(call.entity_type)
            ? call.entity_type
            : null;
          if (entityType != null && !seen.has(id)) {
            seen.add(id);
            idsToLoad.push({ id, entityType });
          }
        }
      });
      if (idsToLoad.length > 0) {
        await Promise.all(
          idsToLoad.map(async ({ id, entityType }) => {
            try {
              const path =
                entityType === 'leads'
                  ? `/crm/leads/${id}`
                  : entityType === 'companies'
                    ? `/crm/companies/${id}`
                    : `/crm/contacts/${id}`;
              if (entityType === 'companies') {
                const { data } = await api.get<{ name?: string | null }>(path);
                entityNames[id] = (data.name && data.name.trim()) ? data.name.trim() : 'Empresa';
              } else {
                const { data } = await api.get<ContactNameResponse>(path);
                entityNames[id] = getContactDisplayName(data);
              }
            } catch {
              // Fallback: mantener sin nombre (se mostrará "Llamada saliente/entrante" o teléfono)
            }
          })
        );
      }

      // Transform to unified CalendarEvent format
      const events: CalendarEvent[] = [];

      // Add appointments
      appointments.forEach((apt) => {
        events.push({
          id: apt.id,
          type: 'appointment',
          title: apt.contact_name
            ? `Cita con ${truncateClientName(apt.contact_name)}`
            : 'Cita sin contacto',
          date: apt.scheduled_at,
          duration: apt.duration,
          contactId: apt.contact_id ?? null,
          contactName: apt.contact_name ?? null,
          delegationId: apt.delegation_id ?? null,
          delegationName: apt.delegation_name ?? null,
          userId: apt.agent_id ?? null,
          metadata: apt,
        });
      });

      // Add calls: "Llamada a [nombre/teléfono]" / "Llamada de [nombre/teléfono]" (truncado); fallback solo si no hay ninguno
      const phoneMaxLen = 16;
      calls.forEach((call) => {
        const isOutbound = call.direction === 'outbound';
        const rawName =
          call.contact_name ||
          (call.contact_id && entityNames[call.contact_id]) ||
          (call.entity_id && entityNames[call.entity_id]) ||
          null;
        const displayName = rawName ? truncateClientName(rawName) : null;
        const phone =
          call.phone?.trim() || (call as { phone_number?: string }).phone_number?.trim() || null;
        const phoneShort = phone
          ? phone.length <= phoneMaxLen
            ? phone
            : phone.slice(0, phoneMaxLen - 1) + '…'
          : null;
        let title: string;
        if (displayName) {
          title = isOutbound
            ? `Llamada a ${displayName}`
            : `Llamada de ${displayName}`;
        } else if (phoneShort) {
          title = isOutbound
            ? `Llamada a ${phoneShort}`
            : `Llamada de ${phoneShort}`;
        } else {
          title = isOutbound ? 'Llamada saliente' : 'Llamada entrante';
        }
        const contactName = rawName ?? call.contact_name;

        events.push({
          id: call.id,
          type: 'call',
          title,
          date: call.created_at,
          duration: call.duration ? Math.floor(call.duration / 60) : undefined,
          contactId: call.contact_id,
          contactName: contactName ?? null,
          userId: call.responsible_user_id ?? null,
          callDirection: call.direction ?? null,
          metadata: call,
        });
      });

      // Add tasks
      tasks.forEach((task) => {
        events.push({
          id: task.id,
          type: 'task',
          title: task.text,
          date: task.complete_till || task.created_at,
          contactId: task.contact_id,
          contactName: task.contact_name,
          userId: task.responsible_user_id ?? null,
          completed: task.is_completed,
          metadata: task,
        });
      });

      // Filtrado client-side por usuario (fallback si el backend no filtra)
      const filteredEvents = params.user_id
        ? events.filter((e) => e.userId === params.user_id)
        : events;

      // Sort by date
      return filteredEvents.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
    },
    staleTime: 60000, // 1 minuto
    // No fallar si hay error, devolver array vacío
    retry: false,
  });
};

/**
 * Hook para obtener solo citas del calendario
 */
export const useAppointmentsCalendar = (params: UseUnifiedCalendarParams) => {
  return useQuery({
    queryKey: ['appointments-calendar', params],
    queryFn: async (): Promise<CalendarEvent[]> => {
      const appointments = await api
        .get<AppointmentSlot[]>('/crm/appointments/calendar', { params })
        .then((r) => r.data)
        .catch((err) => {
          logger.warn('Error fetching appointments calendar:', err);
          return [];
        });

      return appointments.map((apt) => ({
        id: apt.id,
        type: 'appointment' as const,
        title: apt.contact_name
          ? `Cita con ${apt.contact_name}`
          : 'Cita sin contacto',
        date: apt.scheduled_at,
        duration: apt.duration,
        contactId: apt.contact_id ?? null,
        contactName: apt.contact_name ?? null,
        delegationId: apt.delegation_id ?? null,
        delegationName: apt.delegation_name ?? null,
        metadata: apt,
      }));
    },
    staleTime: 60000,
    retry: false,
  });
};
